import {Attendant} from 'organization/Event/Attendants/ViewAttendant/AttendantProvider'
import {organizationApiClient} from 'organization/organizationApiClient'
import React, {useEffect, useState} from 'react'
import {toast} from 'react-toastify'
import {useRouteOrganizationEventId} from 'utils/lib/url'

export interface EventAttendantsContextProps {
  attendants: Attendant[]
  loading: boolean
  page: number
  totalPages: number
  total: number
  from: number
  to: number
  changePage: (e: any, page: number) => void
  searchAtendant: (email: string) => void
  addAttendant: (
    email: string,
    reservation: boolean,
  ) => Promise<string | boolean>
  remove: (attendant: Attendant) => void
}

export const EventAttendantsContext = React.createContext<
  EventAttendantsContextProps | undefined
>(undefined)

export default function EventAttendantsProvider(props: {
  children: React.ReactElement
}) {
  const apiClient = organizationApiClient()
  const [attendants, setAttendants] = useState<Attendant[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [totalPages, setTotalPages] = useState<number>(1)
  const [page, setPage] = useState<number>(1)
  const [total, setTotal] = useState<number>(0)
  const [from, setFrom] = useState<number>(0)
  const [to, setTo] = useState<number>(0)
  const eventId = useRouteOrganizationEventId()
  const [lastSearch, setLastSearch] = useState<string>('')

  useEffect(() => {
    get()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const get = async (getPage?: number, searchEmail?: string) => {
    setLoading(true)
    const selectedPage = getPage ?? page
    const search = searchEmail ?? ''

    await apiClient
      .get(
        `api/event/${eventId}/attendants?page=${selectedPage}&searchEmail=${search}`,
      )
      .then((response) => {
        setAttendants(response.data)
        setTotalPages(response.last_page)
        setPage(response.current_page)
        setTotal(response.total)
        setFrom(response.from)
        setTo(response.to)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const changePage = (e: any, toPage: number) => {
    setPage(toPage)
    get(toPage)
  }

  const addAttendant = async (email: string, reservation: boolean) => {
    setLoading(true)
    const result = await apiClient
      .post(`api/event/${eventId}/attendant`, {email, reservation})
      .then((response) => {
        get()
        if (response?.message?.status === '202') {
          return response?.message?.message
        }

        return true
      })
      .catch((error) => {
        toast.warning(error.response.data.message)

        return false
      })
      .finally(() => {
        setLoading(false)
      })

    return result
  }

  const searchAtendant = async (email: string) => {
    setLastSearch(email)
    return get(1, email)
  }

  const remove = async (attendant: Attendant) => {
    await apiClient
      .remove(`api/event/${eventId}/attendants/${attendant.id}`)
      .then(() => refresh())
      .finally(() => {
        setLoading(false)
      })
  }

  const refresh = () => {
    get(page, lastSearch)
  }

  return (
    <EventAttendantsContext.Provider
      value={{
        attendants,
        loading,
        page,
        changePage,
        totalPages,
        total,
        from,
        to,
        addAttendant,
        searchAtendant,
        remove,
      }}
    >
      {props.children}
    </EventAttendantsContext.Provider>
  )
}

export function useEventAttendants() {
  const context = React.useContext(EventAttendantsContext)
  if (context === undefined) {
    throw new Error(
      'useEventAttendants must be used within EventAttendantsProvider',
    )
  }
  return context
}
